import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save, Loader2, Search, CheckSquare, Square } from 'lucide-react'
import { z } from 'zod'
import { Controller } from 'react-hook-form'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { useZodForm } from '@/hooks/useZodForm'
import { applyApiErrorsToForm } from '@/lib/api-errors'
import { onValidationFail } from '@/lib/form-helpers'
import { api } from '@/services/api/AxiosBaseService'
import { createCrudService } from '@/services/api/createCrudService'
import { normalizeForMatch } from '@/lib/validators'
import { cn } from '@/lib/cn'
import { toast } from '@/components/ui/Toast'

interface Department {
  id: string
  companyId: string
  department: string
  status: 'ATIVO' | 'INATIVO'
  branchIds?: string[]
}
interface BranchOption { id: string; systemName: string }

const DepartmentsService = createCrudService<Department>('/my-company/departments', { searchField: 'department' })

const schema = z.object({
  department: z.string().trim().min(1, 'Informe o nome do departamento.').max(150),
  status:     z.enum(['ATIVO', 'INATIVO']),
})
type FormValues = z.infer<typeof schema>

export function DepartmentFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = !!id
  const [loading, setLoading] = useState(isEdit)
  const [branches, setBranches] = useState<BranchOption[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')

  const form = useZodForm(schema, { defaultValues: { department: '', status: 'ATIVO' } })

  useEffect(() => {
    api.get<{ data: BranchOption[] }>('/my-company/branches', { params: { size: 0, status: 'ATIVO' } })
      .then(r => setBranches(r.data.data)).catch(() => setBranches([]))
  }, [])

  useEffect(() => {
    if (!isEdit || !id) return
    setLoading(true)
    api.get<{ data: Department }>(`/my-company/departments/${id}`)
      .then(r => {
        const d = r.data.data
        form.reset({ department: d.department, status: d.status })
        setSelected(new Set(d.branchIds ?? []))
      })
      .catch(() => toast.error({ title: 'Não encontrado', description: 'Departamento não localizado.' }))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id])

  const filtered = useMemo(() => {
    const q = normalizeForMatch(search)
    return q ? branches.filter(b => normalizeForMatch(b.systemName).includes(q)) : branches
  }, [branches, search])
  const allSelected = filtered.length > 0 && filtered.every(b => selected.has(b.id))

  function toggle(idv: string) {
    setSelected(prev => { const n = new Set(prev); n.has(idv) ? n.delete(idv) : n.add(idv); return n })
  }
  function toggleAll() {
    setSelected(prev => {
      const n = new Set(prev)
      if (allSelected) filtered.forEach(b => n.delete(b.id))
      else filtered.forEach(b => n.add(b.id))
      return n
    })
  }

  async function onSubmit(values: FormValues) {
    try {
      const payload = { department: values.department, status: values.status, branch_ids: Array.from(selected) }
      if (isEdit && id) await DepartmentsService.update(id, payload as Partial<Department>)
      else              await DepartmentsService.create(payload as Partial<Department>)
      toast.success({
        title: isEdit ? 'Departamento atualizado' : 'Departamento criado',
        description: isEdit ? 'As alterações foram salvas.' : 'O novo departamento foi cadastrado.',
      })
      navigate('/my-company/departments')
    } catch (e) {
      applyApiErrorsToForm(e, form.setError, {})
    }
  }

  const errs = form.formState.errors

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={28} className="animate-spin text-gray-400" /></div>
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit, onValidationFail)}>
      <PageHeader
        title={isEdit ? 'Editar departamento' : 'Novo departamento'}
        subtitle="Departamentos da minha empresa"
        crumbs={[{ label: 'Minha empresa' }, { label: 'Departamentos', to: '/my-company/departments' }, { label: isEdit ? 'Editar' : 'Novo' }]}
        backTo="/my-company/departments"
        actions={<Button type="submit" loading={form.formState.isSubmitting} iconLeft={<Save size={14} />}>Salvar</Button>}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">Dados</h2>
          <div className="grid grid-cols-1 gap-4">
            <Input label="Nome do departamento" required {...form.register('department')} error={errs.department?.message} placeholder="Ex.: Financeiro, Recursos Humanos" />
            <Controller name="status" control={form.control} render={({ field }) => (
              <StatusToggle value={field.value} onChange={field.onChange} />
            )} />
          </div>
        </div>

        <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-sm font-semibold text-gray-800">Filiais que possuem este departamento</h2>
            <span className="text-xs text-gray-500">{selected.size} selecionada(s)</span>
          </div>
          <p className="text-xs text-gray-500 mb-3">Cadastre o departamento uma vez e marque todas as filiais onde ele existe.</p>

          <div className="flex items-center gap-2 mb-3">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtrar filiais" iconLeft={<Search size={14} />} clearable wrapperClassName="flex-1" />
            <Button type="button" size="sm" variant="ghost" onClick={toggleAll} disabled={!filtered.length}>
              {allSelected ? 'Desmarcar' : 'Marcar todas'}
            </Button>
          </div>

          {branches.length === 0 ? (
            <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-400">
              Nenhuma filial cadastrada. Cadastre filiais primeiro.
            </div>
          ) : (
            <ul className="max-h-80 overflow-y-auto divide-y divide-gray-100 rounded-md ring-1 ring-gray-200">
              {filtered.map(b => {
                const on = selected.has(b.id)
                return (
                  <li key={b.id}>
                    <button type="button" onClick={() => toggle(b.id)}
                      className={cn('cursor-pointer w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm transition hover:bg-brand-50/40', on && 'bg-brand-50/60')}>
                      {on ? <CheckSquare size={16} className="text-brand-600 shrink-0" /> : <Square size={16} className="text-gray-300 shrink-0" />}
                      <span className="text-gray-800">{b.systemName}</span>
                    </button>
                  </li>
                )
              })}
              {filtered.length === 0 && <li className="px-3 py-6 text-center text-sm text-gray-400">Nenhuma filial para “{search}”.</li>}
            </ul>
          )}
        </div>
      </div>
    </form>
  )
}
